import { Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { getTruncatedText } from "@/utils/text";

interface GroupMenuCategoryTitleProps {
    zones: { name: string; id: string }[];
    className?: string;
}
/**
 * A component that displays a list of zones, truncating with "+N more" when the text would overflow its container
 */
export function GroupMenuCategoryTitle({ zones, className }: GroupMenuCategoryTitleProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [maxWidth, setMaxWidth] = useState<number>(450); // Default width for the title
    const [font, setFont] = useState<string>("14px system-ui");

    // Update maxWidth when size changes
    useEffect(() => {
        let observer: ResizeObserver | undefined;
        if (containerRef.current) {
            observer = new ResizeObserver((entries) => {
                entries.forEach((entry) => {
                    setMaxWidth(entry.contentRect.width);
                });
            });

            observer.observe(containerRef.current);

            // Get computed font style
            const computedStyle = window.getComputedStyle(containerRef.current);
            setFont(`${computedStyle.fontSize} ${computedStyle.fontFamily}`);
        }

        return () => {
            observer?.disconnect();
        };
    }, []);

    // Calculate truncated text
    const { displayText } = useMemo(() => {
        if (!maxWidth) return { displayText: "", wasTruncated: false };

        const zoneNames = zones.map((zone) => zone.name);
        return getTruncatedText(zoneNames, maxWidth, font);
    }, [zones, maxWidth, font]);

    return (
        <Typography
            sx={{ paddingLeft: 2, paddingTop: 2, cursor: "pointer" }}
            variant={"h6"}
            ref={containerRef}
            className={className}
            title={zones.map((zone) => zone.name).join(", ")}
        >
            {displayText}
        </Typography>
    );
}
